import { useEffect, useState } from "react";
import { api } from "@/App";
import { Download as DownloadIcon, Loader2, RefreshCw } from "lucide-react";
import { useTrialGate } from "@/components/TrialGate";

export const BIN_CARDS = [
  { id: "mac-universal", title: "macOS Universal",       note: "x86_64 + arm64 fat · min 10.14 (Intel) / 11.0 (ARM)", featured: true },
  { id: "mac-intel",     title: "macOS Intel",           note: "x86_64 · min macOS 10.14" },
  { id: "mac-arm64",     title: "macOS Apple Silicon",   note: "arm64 · min macOS 11.0" },
  { id: "linux-x86_64",  title: "Linux x86_64",          note: "x86_64 · glibc ≥ 2.31" },
  { id: "linux-arm64",   title: "Linux ARM64",           note: "aarch64 · Jetson / Pi 5 / Resolve-for-Linux" },
  { id: "windows-x64",   title: "Windows",               note: "x64 · Windows 10+" },
];

export const BinaryGrid = ({ prefix, bins, dl, rebuild }) => {
  const gate = useTrialGate(prefix);
  return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BIN_CARDS.map((c) => {
            const tid = `${prefix}${c.id}`;
            const b = bins[tid];
            const avail = b?.available;
            const building = b?.building;
            return (
              <div
                key={tid}
                className={`bg-[#0e1013] border p-4 flex flex-col gap-2 ${
                  c.featured
                    ? "border-[#e69d27] md:col-span-2 lg:col-span-3"
                    : "border-[#2a2f3d]"
                }`}
                data-testid={`binary-${tid}-card`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display uppercase text-lg">{c.title}</span>
                  {c.featured && (
                    <span className="chip text-[#e69d27] border-[#e69d27]">recommended</span>
                  )}
                  <span
                    className={`status-dot ml-auto ${building ? "warn" : avail ? "ok" : "idle"}`}
                    data-testid={`binary-${tid}-dot`}
                  />
                </div>
                <div className="font-mono text-[11px] text-[#9ba3b4] leading-5">
                  {c.note}
                  <br />
                  {avail ? (
                    <>
                      size {(b.size_bytes / 1024).toFixed(0)} KB · built{" "}
                      {new Date(b.built_at).toLocaleString()}
                    </>
                  ) : building ? (
                    <span className="text-[#e69d27]">building now…</span>
                  ) : (
                    <span className="text-[#5c6479]">not built yet</span>
                  )}
                </div>
                <div className="font-mono text-[11px] flex gap-3" data-testid={`binary-${tid}-downloads`}>
                  <span className="text-[#f2f4f8]">{(dl?.targets?.[tid]?.total ?? 0).toLocaleString()} downloads</span>
                  <span className="text-[#e69d27]">+{(dl?.targets?.[tid]?.week ?? 0).toLocaleString()} this week</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {avail && (
                    <button
                      type="button"
                      onClick={() => gate.download(tid)}
                      className="btn-amber px-3 py-2 text-xs flex-1 flex items-center justify-center gap-2"
                      data-testid={`binary-${tid}-download`}
                    >
                      <DownloadIcon size={14} />
                      download
                    </button>
                  )}
                  <button
                    className="btn-ghost px-3 py-2 text-xs flex items-center justify-center gap-2"
                    disabled={building}
                    onClick={() => rebuild(tid)}
                    data-testid={`binary-${tid}-rebuild`}
                    title="rebuild latest from source"
                  >
                    {building ? <Loader2 className="spin" size={12} /> : <RefreshCw size={12} />}
                    {building ? "building…" : "rebuild"}
                  </button>
                </div>
              </div>
            );
          })}
          {gate.dialog}
        </div>
  );
};

export function useBins(prefixes) {
  const [bins, setBins] = useState({});
  const [dl, setDl] = useState(null);
  useEffect(() => {
    let alive = true;
    const targets = BIN_CARDS.flatMap((c) => prefixes.map((p) => `${p}${c.id}`));
    const poll = async () => {
      const results = await Promise.all(targets.map((t) => api.get(`/binary/${t}/status`).then((r) => [t, r.data]).catch(() => [t, null])));
      if (alive) setBins(Object.fromEntries(results));
      try { const r = await api.get("/downloads/stats"); if (alive) setDl(r.data); } catch { /* noop */ }
    };
    poll();
    const int = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(int); };
  }, [prefixes.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const rebuild = async (target) => {
    try {
      await api.post(`/binary/${target}/rebuild`);
      setBins((x) => ({ ...x, [target]: { ...x[target], building: true } }));
    } catch { /* noop */ }
  };
  return { bins, dl, rebuild };
}
